import * as React from 'react';
import { Box, Grid, Paper, Skeleton, Typography, Divider, LinearProgress } from '@mui/material';
import {
  AccountBalanceWallet, MonetizationOn, Description, Lock,
  CreditCard, PeopleAlt, AccountBalance, TrendingUp,
  HealthAndSafety, Savings,
} from '@mui/icons-material';
import { Tag } from 'antd';
import {
  BarChart as ReBarChart, Bar, PieChart as RePieChart, Pie, Cell,
  LineChart as ReLineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer,
} from 'recharts';

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];
const PRODUIT_COLORS: Record<string, string> = {
  QARD: '#3b82f6',
  SALAM: '#10b981',
  MOURABAHA: '#f59e0b',
  IJARA: '#8b5cf6',
};

const tooltipStyle = {
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: 12,
  backgroundColor: '#fff',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
};

const fmtMoney = (v: any) =>
  v != null ? Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0';
const fmtPct = (v: number) => (v != null ? Number(v).toFixed(1) + '%' : '0%');
const hasData = (arr: any[] | null | undefined) => Array.isArray(arr) && arr.length > 0;

interface Tab8Props {
  form?: any;
  dataStats?: any;
  loading?: boolean;
}

const Tab8 = ({ form, dataStats, loading }: Tab8Props) => {
  const kpi = hasData(dataStats?.kpi) ? dataStats.kpi[0] : null;

  const repartition = React.useMemo(
    () => (dataStats?.repartition ?? []).filter((e: any) => (e.NbDossiers || 0) > 0),
    [dataStats]
  );

  // Dossiers par statut : pivot {Statut, QARD, SALAM, MOURABAHA, IJARA}
  const statutsGrouped = React.useMemo(() => {
    const map: Record<string, any> = {};
    (dataStats?.statuts ?? []).forEach((e: any) => {
      const statut = e.Statut ?? '-';
      if (!map[statut]) map[statut] = { Statut: statut };
      map[statut][e.CodeFinancement] = (map[statut][e.CodeFinancement] || 0) + (e.NbDossiers || 0);
    });
    return Object.values(map);
  }, [dataStats]);

  const statutLabels = React.useMemo(
    () => statutsGrouped.map((e: any) => e.Statut),
    [statutsGrouped]
  );

  const remboursement = React.useMemo(() => {
    const rep = dataStats?.repartition ?? [];
    return (dataStats?.remboursement ?? []).filter((e: any) => {
      const nbDossiers = rep.find((r: any) => r.CodeFinancement === e.Produit)?.NbDossiers || 0;
      return (e.TotalDu || 0) > 0 || (e.TotalPaye || 0) > 0 || nbDossiers > 0;
    });
  }, [dataStats]);

  // Top 15 comptes par solde pour lisibilité du graphique
  const topComptes = React.useMemo(
    () =>
      [...(dataStats?.soldesComptes ?? [])]
        .sort((a: any, b: any) => (b.Solde || 0) - (a.Solde || 0))
        .slice(0, 15)
        .map((e: any) => ({ ...e })),
    [dataStats]
  );

  // Top opérations regroupées par type (CAISSE / BACKOFFICE)
  const topOpsParType = React.useMemo(() => {
    const byOpe: Record<string, any> = {};
    (dataStats?.topOperations ?? []).forEach((e: any) => {
      const key = e.CodeOpe ?? '?';
      if (!byOpe[key]) {
        const lib = e.Libelle || e.CodeOpe || '';
        byOpe[key] = {
          CodeOpe: e.CodeOpe, Libelle: lib,
          LibelleCourt: lib.length > 30 ? lib.substring(0, 30) + '…' : lib,
          CAISSE: 0, BACKOFFICE: 0, MontantTotal: 0, total: 0,
        };
      }
      const type = e.TypeOperation === 'CAISSE' ? 'CAISSE' : 'BACKOFFICE';
      byOpe[key][type] += e.NbOperations || 0;
      byOpe[key].total += e.NbOperations || 0;
      byOpe[key].MontantTotal += e.MontantTotal || 0;
    });
    return Object.values(byOpe).sort((a: any, b: any) => b.total - a.total).slice(0, 10);
  }, [dataStats]);

  // Soldes par plan comptable
  const soldesPlans = React.useMemo(
    () =>
      (dataStats?.soldesPlans ?? []).map((e: any) => ({
        ...e,
        Plan: `${e.IDPLAN} - ${(e.LibellePlan || '').substring(0, 24)}`,
      })),
    [dataStats]
  );

  // Top comptes par solde max (limite la lisibilité des courbes)
  const topCptKeys = React.useMemo(() => {
    const agg: Record<string, number> = {};
    (dataStats?.evolutionParCompte ?? []).forEach((e: any) => {
      agg[e.NUMCPT] = Math.max(agg[e.NUMCPT] || 0, Math.abs(e.Solde || 0));
    });
    return Object.entries(agg).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k]) => k);
  }, [dataStats]);

  const cptLabel = (c: string) => '…' + String(c).split('-').slice(-2).join('-');

  // Évolution du solde par compte : pivot {Date, [NUMCPT]: Solde}
  const evolutionParCompte = React.useMemo(() => {
    const byDate: Record<string, any> = {};
    (dataStats?.evolutionParCompte ?? []).forEach((e: any) => {
      if (!topCptKeys.includes(e.NUMCPT)) return;
      if (!byDate[e.Date]) byDate[e.Date] = { Date: e.Date };
      byDate[e.Date][e.NUMCPT] = e.Solde;
    });
    return Object.values(byDate).sort((a: any, b: any) => a.Date.localeCompare(b.Date));
  }, [dataStats, topCptKeys]);

  // Top comptes par volume de mouvements (pour les charts mensuels)
  const topCptMvt = React.useMemo(() => {
    const agg: Record<string, number> = {};
    (dataStats?.mensuelParCompte ?? []).forEach((e: any) => {
      agg[e.NUMCPT] = (agg[e.NUMCPT] || 0) + (e.MontantDebit || 0) + (e.MontantCredit || 0);
    });
    return Object.entries(agg).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k]) => k);
  }, [dataStats]);

  // Débit / crédit mensuel par compte : pivot {Mois, [NUMCPT]: montant}
  const mensuelDebitParCompte = React.useMemo(() => {
    const byMois: Record<string, any> = {};
    (dataStats?.mensuelParCompte ?? []).forEach((e: any) => {
      if (!topCptMvt.includes(e.NUMCPT)) return;
      if (!byMois[e.Mois]) byMois[e.Mois] = { Mois: e.Mois };
      byMois[e.Mois][e.NUMCPT] = e.MontantDebit;
    });
    return Object.values(byMois).sort((a: any, b: any) => a.Mois.localeCompare(b.Mois));
  }, [dataStats, topCptMvt]);

  const mensuelCreditParCompte = React.useMemo(() => {
    const byMois: Record<string, any> = {};
    (dataStats?.mensuelParCompte ?? []).forEach((e: any) => {
      if (!topCptMvt.includes(e.NUMCPT)) return;
      if (!byMois[e.Mois]) byMois[e.Mois] = { Mois: e.Mois };
      byMois[e.Mois][e.NUMCPT] = e.MontantCredit;
    });
    return Object.values(byMois).sort((a: any, b: any) => a.Mois.localeCompare(b.Mois));
  }, [dataStats, topCptMvt]);

  const numcli = form?.getFieldValue?.('NUMCLI');
  const nomcli = form?.getFieldValue?.('lib_NUMCLI');

  if (loading && !dataStats) {
    return (
      <div className='tabItem'>
        <Box sx={{ p: 2, position: 'absolute', width: '85%' }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rounded" height={120} />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rounded" height={300} />
        </Box>
      </div>
    );
  }

  if (!dataStats) {
    return (
      <div className='tabItem'>
        <Box sx={{ p: 4, textAlign: 'center', position: 'absolute', width: '85%' }}>
          <AccountBalance sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
          <Typography variant="h6" color="textSecondary">
            {numcli ? 'Aucune donnée statistique disponible pour ce client' : 'Sélectionnez un client pour afficher ses statistiques'}
          </Typography>
          {!numcli && (
            <Typography variant="body2" color="textSecondary">
              Recherchez un client pour charger automatiquement ses statistiques
            </Typography>
          )}
        </Box>
      </div>
    );
  }

  const kpiCards = [
    {
      icon: <AccountBalanceWallet />, label: 'Comptes', value: kpi?.NbComptes ?? 0,
      sub: `Solde veille: ${fmtMoney(kpi?.TotalSoldeVeille)}`,
      color: '#3b82f6', bg: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
    },
    {
      icon: <MonetizationOn />, label: 'Total disponible', value: fmtMoney(kpi?.TotalDisponible),
      sub: 'Tous comptes confondus',
      color: '#10b981', bg: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
    },
    {
      icon: <Description />, label: 'Encours financement', value: fmtMoney(kpi?.EncoursFinancement),
      sub: `${kpi?.NbDossiersFinancement ?? 0} dossiers · Restant: ${fmtMoney(kpi?.SoldeRestantFinancement)}`,
      color: '#f59e0b', bg: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
    },
    {
      icon: <Lock />, label: 'Montant bloqué', value: fmtMoney(kpi?.TotalMontantBloque),
      sub: `${kpi?.NbMontantBloque ?? 0} blocage(s) actif(s)`,
      color: '#ef4444', bg: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
    },
    {
      icon: <CreditCard />, label: 'Facilités', value: kpi?.NbFacilites ?? 0,
      sub: `${kpi?.NbOrdresPermanents ?? 0} ordre(s) permanent(s)`,
      color: '#8b5cf6', bg: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
    },
    {
      icon: <PeopleAlt />, label: 'Relations', value: kpi?.NbSignataires ?? 0,
      sub: 'Signataires sur l\'ensemble des comptes',
      color: '#06b6d4', bg: 'linear-gradient(135deg, #ecfeff, #cffafe)',
    },
    {
      icon: <PeopleAlt />, label: 'Procurations', value: kpi?.NbProcurations ?? 0,
      sub: 'Mandataires sur l\'ensemble des comptes',
      color: '#0ea5e9', bg: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
    },
    {
      icon: <HealthAndSafety />, label: 'SafeCare', value: kpi?.NbSouscriptionsSafeCare ?? 0,
      sub: `${kpi?.NbSouscriptionsActives ?? 0} active(s) · Cotis. payées: ${fmtMoney(kpi?.TotalCotisationsPayees)}`,
      color: '#14b8a6', bg: 'linear-gradient(135deg, #f0fdfa, #ccfbf1)',
    },
    {
      icon: <Savings />, label: 'Bons de caisse', value: kpi?.NbBdc ?? 0,
      sub: `Montant: ${fmtMoney(kpi?.MontantTotalBdc)} · ${kpi?.NbDemandesInterBdc ?? 0} interr.`,
      color: '#d97706', bg: 'linear-gradient(135deg, #fffbeb, #fef9c3)',
    },
  ];

  const finKpiCards = [
    { label: 'Financements sains', value: kpi?.NbFinSains ?? 0, color: '#10b981', sub: 'Statut ACTIF' },
    { label: 'Financements impayés', value: kpi?.NbFinImpayes ?? 0, color: '#ef4444', sub: `Montant: ${fmtMoney(kpi?.MontantTotalImpaye)}` },
    { label: 'Financements terminés', value: kpi?.NbFinTermines ?? 0, color: '#64748b', sub: 'TERMINE / LIVRE' },
    { label: 'Montant impayé total', value: fmtMoney(kpi?.MontantTotalImpaye), color: '#f59e0b', sub: 'Qard + Mourabaha + Ijara' },
  ];

  return (
    <div className='tabItem'>
      <Box sx={{ p: 2, position: 'absolute', width: '85%' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: '#f8fafc' }}>
        <Typography variant="h6" fontWeight="bold">
          Statistiques client {numcli ? `· ${numcli}${nomcli ? ' - ' + nomcli : ''}` : ''}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Vue synthétique des comptes, financements et de l'activité sur 12 mois
        </Typography>
      </Paper>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper
              elevation={1}
              sx={{
                p: 2, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column',
                justifyContent: 'space-between', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 },
              }}
            >
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 2, bgcolor: card.bg, color: card.color }}>
                  {card.icon}
                </Box>
                <Typography variant="body2" color="textSecondary" fontWeight={600}>
                  {card.label}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: card.color, lineHeight: 1.2 }}>
                {card.value}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                {card.sub}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ================= FINANCEMENTS ================= */}
      {hasData(repartition) && (
        <Box sx={{ mb: 3 }}>
          <Divider sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">Financements</Typography>
          </Divider>

          {/* Santé du portefeuille */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {finKpiCards.map((card, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Paper elevation={1} sx={{ p: 1.5, borderRadius: 3, borderLeft: `4px solid ${card.color}`, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                  <Typography variant="caption" color="textSecondary" fontWeight={600}>
                    {card.label}
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: card.color }}>
                    {card.value}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {card.sub}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            {/* Donut répartition par produit */}
            <Grid item xs={12} md={4}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: 340, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Répartition par produit
                </Typography>
                <ResponsiveContainer width="100%" height="88%">
                  <RePieChart>
                    <Pie
                      data={repartition}
                      dataKey="NbDossiers"
                      nameKey="CodeFinancement"
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={90}
                      paddingAngle={2}
                      label={(e: any) => `${e.CodeFinancement} (${e.NbDossiers})`}
                      fontSize={11}
                    >
                      {repartition.map((e: any, i: number) => (
                        <Cell key={i} fill={PRODUIT_COLORS[e.CodeFinancement] || CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RTooltip contentStyle={tooltipStyle} formatter={(v: any) => [v, 'Dossiers']} />
                    <Legend />
                  </RePieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Dossiers par statut */}
            {hasData(statutsGrouped) && (
              <Grid item xs={12} md={4}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: 340, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Dossiers par statut
                  </Typography>
                  <ResponsiveContainer width="100%" height="88%">
                    <ReBarChart data={statutsGrouped}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="Statut" fontSize={11} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <RTooltip contentStyle={tooltipStyle} />
                      <Legend />
                      {Object.keys(PRODUIT_COLORS).map((prod) => (
                        <Bar key={prod} dataKey={prod} stackId="statut" fill={PRODUIT_COLORS[prod]} radius={[3, 3, 0, 0]} />
                      ))}
                    </ReBarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            )}

            {/* Montants par produit */}
            <Grid item xs={12} md={hasData(statutsGrouped) ? 4 : 8}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: 340, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Montants par produit
                </Typography>
                <ResponsiveContainer width="100%" height="88%">
                  <ReBarChart data={repartition}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="CodeFinancement" fontSize={11} />
                    <YAxis fontSize={11} tickFormatter={(v: any) => fmtMoney(v)} width={80} />
                    <RTooltip contentStyle={tooltipStyle} formatter={(v: any) => [fmtMoney(v), 'Montant']} />
                    <Bar dataKey="MontantTotal" name="Montant total" radius={[4, 4, 0, 0]}>
                      {repartition.map((e: any, i: number) => (
                        <Cell key={i} fill={PRODUIT_COLORS[e.CodeFinancement] || CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </ReBarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Progression des remboursements */}
          {hasData(remboursement) && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {remboursement.map((p: any, i: number) => {
                const pct = p.TotalDu > 0 ? Math.min(100, (p.TotalPaye / p.TotalDu) * 100) : 0;
                return (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 3, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">{p.Produit}</Typography>
                        <Tag color="blue">{fmtPct(pct)}</Tag>
                      </Box>
                      <LinearProgress
                        variant="determinate" value={pct}
                        sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: PRODUIT_COLORS[p.Produit] || '#3b82f6' } }}
                      />
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                        {p.TotalDu > 0 ? `Payé: ${fmtMoney(p.TotalPaye)} / Dû: ${fmtMoney(p.TotalDu)}` : 'Aucune échéance enregistrée'}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* ================= COMPTES ================= */}
      {hasData(topComptes) && (
        <Box sx={{ mb: 3 }}>
          <Divider sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">Comptes</Typography>
          </Divider>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Soldes par compte {dataStats?.soldesComptes?.length > 15 ? '(top 15)' : ''}
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <ReBarChart data={topComptes}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="NUMCPT" fontSize={9} angle={-45} textAnchor="end" height={90} interval={0} />
                    <YAxis fontSize={11} tickFormatter={(v: any) => fmtMoney(v)} width={80} />
                    <RTooltip
                      contentStyle={tooltipStyle}
                      formatter={(v: any, name: any) => [fmtMoney(v), name]}
                      labelFormatter={(l: any, payload: any) => payload?.[0]?.payload?.NOMCPT || l}
                    />
                    <Legend />
                    <Bar dataKey="Solde" name="Disponible" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="SoldeVeille" name="Solde veille" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                  </ReBarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Solde par plan comptable */}
            {hasData(soldesPlans) && (
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 3, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Solde par plan comptable
                  </Typography>
                  <ResponsiveContainer width="100%" height={Math.max(240, soldesPlans.length * 38)}>
                    <ReBarChart data={soldesPlans} layout="vertical" margin={{ left: 20, right: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" fontSize={11} tickFormatter={(v: any) => fmtMoney(v)} />
                      <YAxis type="category" dataKey="Plan" fontSize={10} width={280} />
                      <RTooltip
                        contentStyle={tooltipStyle}
                        formatter={(v: any, name: any) => [fmtMoney(v), name]}
                      />
                      <Legend />
                      <Bar dataKey="SoldeTotal" name="Solde disponible" radius={[0, 4, 4, 0]}>
                        {soldesPlans.map((e: any, i: number) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </ReBarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* ================= ACTIVITÉ ================= */}
      {(hasData(evolutionParCompte) || hasData(mensuelDebitParCompte) || hasData(topOpsParType)) && (
        <Box sx={{ mb: 2 }}>
          <Divider sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">Activité des comptes (12 mois)</Typography>
          </Divider>
          <Grid container spacing={2}>
            {/* Évolution du solde par compte */}
            {hasData(evolutionParCompte) && (
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 3, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <TrendingUp sx={{ color: '#3b82f6', fontSize: 20 }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Évolution du solde par compte {dataStats?.evolutionParCompte && new Set(dataStats.evolutionParCompte.map((e: any) => e.NUMCPT)).size > 8 ? '(top 8 comptes)' : ''}
                    </Typography>
                  </Box>
                  <ResponsiveContainer width="100%" height={320}>
                    <ReLineChart data={evolutionParCompte}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="Date" fontSize={10} />
                      <YAxis fontSize={11} tickFormatter={(v: any) => fmtMoney(v)} width={90} />
                      <RTooltip
                        contentStyle={tooltipStyle}
                        formatter={(v: any, name: any) => [fmtMoney(v), cptLabel(name)]}
                      />
                      <Legend formatter={(v: any) => cptLabel(v)} />
                      {topCptKeys.map((cpt: string, i: number) => (
                        <Line
                          key={cpt}
                          type="monotone"
                          dataKey={cpt}
                          name={cpt}
                          stroke={CHART_COLORS[i % CHART_COLORS.length]}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      ))}
                    </ReLineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            )}

            {/* Débit mensuel par compte */}
            {hasData(mensuelDebitParCompte) && (
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: 340, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Débit par mois et par compte
                  </Typography>
                  <ResponsiveContainer width="100%" height="88%">
                    <ReBarChart data={mensuelDebitParCompte}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="Mois" fontSize={10} />
                      <YAxis fontSize={11} tickFormatter={(v: any) => fmtMoney(v)} width={80} />
                      <RTooltip contentStyle={tooltipStyle} formatter={(v: any, name: any) => [fmtMoney(v), cptLabel(name)]} />
                      <Legend formatter={(v: any) => cptLabel(v)} />
                      {topCptMvt.map((cpt: string, i: number) => (
                        <Bar key={cpt} dataKey={cpt} name={cpt} stackId="debit" fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </ReBarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            )}

            {/* Crédit mensuel par compte */}
            {hasData(mensuelCreditParCompte) && (
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: 340, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Crédit par mois et par compte
                  </Typography>
                  <ResponsiveContainer width="100%" height="88%">
                    <ReBarChart data={mensuelCreditParCompte}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="Mois" fontSize={10} />
                      <YAxis fontSize={11} tickFormatter={(v: any) => fmtMoney(v)} width={80} />
                      <RTooltip contentStyle={tooltipStyle} formatter={(v: any, name: any) => [fmtMoney(v), cptLabel(name)]} />
                      <Legend formatter={(v: any) => cptLabel(v)} />
                      {topCptMvt.map((cpt: string, i: number) => (
                        <Bar key={cpt} dataKey={cpt} name={cpt} stackId="credit" fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </ReBarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            )}

            {/* Top opérations par type */}
            {hasData(topOpsParType) && (
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 2, borderRadius: 3, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Top opérations par type (nombre d'opérations)
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <ReBarChart data={topOpsParType} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" fontSize={11} allowDecimals={false} />
                      <YAxis type="category" dataKey="LibelleCourt" fontSize={10} width={240} />
                      <RTooltip
                        contentStyle={tooltipStyle}
                        content={({ payload }: any) => {
                          const p = payload?.[0]?.payload;
                          if (!p) return null;
                          return (
                            <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1, p: 1, fontSize: 12, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                              <Typography variant="caption" fontWeight={600} display="block">
                                {p.CodeOpe} - {p.Libelle}
                              </Typography>
                              <Typography variant="caption" display="block">Caisse : {p.CAISSE} · Back-office : {p.BACKOFFICE}</Typography>
                              <Typography variant="caption" display="block">Montant total : {fmtMoney(p.MontantTotal)}</Typography>
                            </Box>
                          );
                        }}
                      />
                      <Legend />
                      <Bar dataKey="CAISSE" name="Caisse" stackId="ops" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="BACKOFFICE" name="Back-office" stackId="ops" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    </ReBarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Aucune donnée du tout */}
      {!kpi && !hasData(repartition) && !hasData(topComptes) && !hasData(evolutionParCompte) && (
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, textAlign: 'center', bgcolor: '#f8fafc' }}>
          <Typography variant="body1" color="textSecondary">
            Aucune donnée statistique disponible pour ce client
          </Typography>
        </Paper>
      )}
      </Box>
    </div>
  );
};

export default Tab8;
